import JSZip from "jszip";

// Junk entries some OSes/zip tools add automatically — not real resume files.
const ZIP_IGNORED_ENTRY_PATTERN = /(^__MACOSX\/|\/__MACOSX\/|\.DS_Store$|Thumbs\.db$)/i;

// Returns true if every real (non-junk, non-folder) entry in the zip is a PDF.
// If the zip can't be read at all, we don't block the upload here — the
// backend performs its own validation on the actual file contents.
export const validateZipContainsOnlyPdfs = async (file) => {
  try {
    const zip = await JSZip.loadAsync(file);
    return Object.values(zip.files).every((entry) => {
      if (entry.dir || ZIP_IGNORED_ENTRY_PATTERN.test(entry.name)) return true;
      const ext = entry.name.split(".").pop()?.toLowerCase();
      return ext === "pdf";
    });
  } catch {
    return true;
  }
};

export const ATS_MATCH_LABELS = {
  EXCELLENT: "Excellent Match",
  STRONG:    "Strong Match",
  GOOD:      "Good Match",
  AVERAGE:   "Average",
  POOR:      "Poor",
};

export const getAtsMatchLabel = (score, backendLabel) => {
  if (backendLabel) return backendLabel;
  const s = Number(score) || 0;
  if (s >= 90) return ATS_MATCH_LABELS.EXCELLENT;
  if (s >= 80) return ATS_MATCH_LABELS.STRONG;
  if (s >= 70) return ATS_MATCH_LABELS.GOOD;
  if (s >= 50) return ATS_MATCH_LABELS.AVERAGE;
  return ATS_MATCH_LABELS.POOR;
};

export const getAtsMatchBadgeClass = (label) => {
  switch (label) {
    case ATS_MATCH_LABELS.EXCELLENT: return "hiring-ats-badge hiring-ats-badge--excellent";
    case ATS_MATCH_LABELS.STRONG:    return "hiring-ats-badge hiring-ats-badge--strong";
    case ATS_MATCH_LABELS.GOOD:      return "hiring-ats-badge hiring-ats-badge--good";
    case ATS_MATCH_LABELS.AVERAGE:   return "hiring-ats-badge hiring-ats-badge--average";
    default:                         return "hiring-ats-badge hiring-ats-badge--poor";
  }
};

export const getAtsScoreColor = (score) => {
  const s = Number(score) || 0;
  if (s >= 80) return "#28a745";
  if (s >= 60) return "#f39c12";
  return "#dc3545";
};

export const toListString = (value) => {
  if (!value) return "";
  if (Array.isArray(value)) return value.filter(Boolean).join(", ");
  return String(value);
};

export const toArray = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter(Boolean);
  return [String(value)];
};

/* ── Shared field validators ─────────────────────────────────
 * Single source of truth for the "text fields must contain letters,
 * numeric fields must contain only numbers" rule across every hiring
 * form (create/edit job, add/edit candidate, resume-import review).
 */

export const EMAIL_PATTERN  = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const MOBILE_PATTERN = /^[+]?[\d\s()-]{7,20}$/;

/** True when a value is digits-only (e.g. "452345") with no letters at all. */
export const isPurelyNumeric = (value) => {
  const str = String(value ?? "").trim();
  return !!str && /\d/.test(str) && !/[a-zA-Z]/.test(str);
};

/**
 * Validates a free-text field (job title, description, candidate name, ...):
 * required check, plus a guard against saving values that are only digits —
 * e.g. a job title of "452345" — since these fields should describe
 * something, not just count it.
 */
export const validateTextField = (value, label, { required = true } = {}) => {
  const str = String(value ?? "").trim();
  if (!str) return required ? `${label} is required` : "";
  if (isPurelyNumeric(str)) return `${label} cannot contain only numbers`;
  return "";
};

/**
 * Validates a numeric field (vacancies, experience, CTC, notice period, ...):
 * required check, must-be-a-number check, non-negative, optional integer-only.
 */
export const validateNumericField = (value, label, { required = false, integer = false, min = 0 } = {}) => {
  const str = String(value ?? "").trim();
  if (!str) return required ? `${label} is required` : "";
  if (!/^\d*\.?\d+$/.test(str)) return `${label} must contain only numbers`;
  const num = Number(str);
  if (Number.isNaN(num)) return `${label} must contain only numbers`;
  if (num < min) return min > 0 ? `${label} must be at least ${min}` : `${label} cannot be negative`;
  if (integer && !Number.isInteger(num)) return `${label} must be a whole number`;
  return "";
};

export const validateEmailField = (value, { required = true } = {}) => {
  const str = String(value ?? "").trim();
  if (!str) return required ? "Email is required" : "";
  if (!EMAIL_PATTERN.test(str)) return "Enter a valid email address";
  return "";
};

export const validateMobileField = (value, { required = true } = {}) => {
  const str = String(value ?? "").trim();
  if (!str) return required ? "Mobile number is required" : "";
  if (!MOBILE_PATTERN.test(str)) return "Enter a valid mobile number";
  return "";
};

/**
 * Some resume-parsing responses serialize list/dict fields using Python's
 * repr() instead of JSON, e.g. "[{'company': 'Acme', 'role': \"Alice's Intern\"}]".
 * Python's repr() switches a string to double quotes whenever it contains an
 * apostrophe, so a naive "replace every ' with \"" regex misreads apostrophes
 * inside double-quoted strings as new string boundaries and corrupts the
 * whole thing. This is a small hand-written parser that tracks quote context
 * character-by-character instead, so it handles mixed quoting correctly.
 */
const parsePythonLiteral = (str) => {
  let i = 0;

  const skipWs = () => { while (i < str.length && /\s/.test(str[i])) i++; };

  const parseString = () => {
    const quote = str[i];
    i++;
    let out = "";
    while (i < str.length && str[i] !== quote) {
      if (str[i] === "\\" && i + 1 < str.length) {
        const esc = str[i + 1];
        out += ({ n: "\n", t: "\t", r: "\r" })[esc] ?? esc;
        i += 2;
      } else {
        out += str[i];
        i++;
      }
    }
    i++; // closing quote
    return out;
  };

  const parseNumber = () => {
    const start = i;
    while (i < str.length && /[0-9.eE+-]/.test(str[i])) i++;
    const num = Number(str.slice(start, i));
    return Number.isNaN(num) ? null : num;
  };

  const parseArray = () => {
    i++; // [
    const arr = [];
    skipWs();
    if (str[i] === "]") { i++; return arr; }
    while (i < str.length) {
      arr.push(parseValue());
      skipWs();
      if (str[i] === ",") { i++; skipWs(); continue; }
      break;
    }
    if (str[i] === "]") i++;
    return arr;
  };

  const parseObject = () => {
    i++; // {
    const obj = {};
    skipWs();
    if (str[i] === "}") { i++; return obj; }
    while (i < str.length) {
      skipWs();
      const key = parseValue();
      skipWs();
      if (str[i] === ":") i++;
      skipWs();
      obj[key] = parseValue();
      skipWs();
      if (str[i] === ",") { i++; skipWs(); continue; }
      break;
    }
    if (str[i] === "}") i++;
    return obj;
  };

  const parseValue = () => {
    skipWs();
    const c = str[i];
    if (c === "'" || c === '"') return parseString();
    if (c === "[") return parseArray();
    if (c === "{") return parseObject();
    if (str.startsWith("None", i))  { i += 4; return null; }
    if (str.startsWith("True", i))  { i += 4; return true; }
    if (str.startsWith("False", i)) { i += 5; return false; }
    return parseNumber();
  };

  try {
    return parseValue();
  } catch {
    return null;
  }
};

const formatEntry = (item) => {
  if (item == null) return "";
  if (typeof item !== "object") return String(item);

  const range = [item.start_date, item.end_date].filter(Boolean).join(" – ") || item.date || item.duration;

  // Education entries: degree + institution (+ percentage, dates)
  if (item.degree || item.institution) {
    let out = [item.degree, item.institution].filter(Boolean).join(", ");
    if (range) out += `${out ? " " : ""}(${range})`;
    if (item.percentage) out += `${out ? " – " : ""}${item.percentage}`;
    return out || Object.values(item).filter(Boolean).join(", ");
  }

  // Work experience: company + designation (+ dates)
  if (item.company) {
    let out = [item.company, item.designation].filter(Boolean).join(", ");
    if (range) out += `${out ? " " : ""}(${range})`;
    return out || Object.values(item).filter(Boolean).join(", ");
  }

  // Certifications / projects / generic: name (+ date/duration) (+ description)
  const title = item.name || item.title;
  if (title) {
    let out = title;
    if (range) out += ` (${range})`;
    if (item.description) out += `: ${item.description}`;
    return out;
  }

  return Object.values(item).filter(Boolean).join(", ");
};

/**
 * Formats a list-of-objects field (previous companies, certifications,
 * projects, ...) into a readable comma/semicolon-joined string. Handles
 * real arrays, single objects, and Python-repr strings from the API.
 */
export const toEntryListString = (value) => {
  if (!value) return "";

  let list = value;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!/^[[{][\s\S]*[\]}]$/.test(trimmed)) return value;
    const parsed = parsePythonLiteral(trimmed);
    if (parsed == null) return value;
    list = parsed;
  }

  if (Array.isArray(list)) return list.map(formatEntry).filter(Boolean).join("; ");
  if (typeof list === "object") return formatEntry(list);
  return String(list);
};

export const normalizeAtsBreakdown = (raw = {}) => ({
  mandatorySkills:  toListString(raw.mandatory_skills  ?? raw.mandatorySkills),
  matchedSkills:    toListString(raw.matched_skills    ?? raw.matchedSkills),
  missingSkills:    toListString(raw.missing_skills    ?? raw.missingSkills),
  preferredSkills:  toListString(raw.preferred_skills  ?? raw.preferredSkills),
  experience:       raw.experience       ?? raw.experience_score       ?? "",
  education:        raw.education        ?? raw.education_score        ?? "",
  certifications:   raw.certifications   ?? raw.certifications_score   ?? "",
  projects:         raw.projects         ?? raw.projects_score         ?? "",
  finalScore:       raw.final_score      ?? raw.finalScore             ?? raw.score ?? "",
  matchLevel:       raw.match_level      ?? raw.matchLevel             ?? "",
  scoringMethod:    raw.scoring_method   ?? raw.scoringMethod          ?? "",
  strengths:        toArray(raw.strengths),
  weaknesses:       toArray(raw.weaknesses),
});

export const normalizeParsedCandidate = (raw = {}) => ({
  parseId:           raw.parse_id           ?? raw.parseId           ?? null,
  name:              raw.name               ?? "",
  email:             raw.email              ?? "",
  phone:             raw.phone              ?? raw.mobile            ?? "",
  skills:            toListString(raw.skills),
  experience:        raw.experience         ?? raw.total_experience  ?? "",
  education:         toEntryListString(raw.education),
  previousCompanies: toEntryListString(raw.previous_companies ?? raw.previousCompanies),
  certifications:    toEntryListString(raw.certifications),
  projects:          toEntryListString(raw.projects),
  designation:       raw.designation        ?? "",
  currentCTC:        raw.current_ctc        ?? raw.currentCTC        ?? "",
  expectedCTC:       raw.expected_ctc       ?? raw.expectedCTC       ?? "",
  noticePeriod:      raw.notice_period      ?? raw.noticePeriod      ?? "",
  atsScore:          raw.ats_score          ?? raw.atsScore          ?? 0,
  atsMatchLabel:     getAtsMatchLabel(raw.ats_score ?? raw.atsScore, raw.ats_match_label ?? raw.atsMatchLabel),
  resumeUrl:         raw.resume_url         ?? raw.resumeUrl         ?? raw.resume_preview_url ?? "",
  resumeFileName:    raw.resume_file_name   ?? raw.resumeFileName    ?? "",
  atsBreakdown:      normalizeAtsBreakdown(raw.ats_breakdown ?? raw.atsBreakdown ?? {}),
  atsScoreReason:    raw.ats_score_reason   ?? raw.atsScoreReason    ?? "",
  aiRecommendation:  raw.recommendation     ?? raw.aiRecommendation  ?? "",
});

export const buildCreateCandidatePayload = (form, parseMeta = {}) => {
  const payload = {
    name:               form.name?.trim(),
    email:              form.email?.trim(),
    phone:              form.phone?.trim(),
    skills:             form.skills,
    experience:         form.experience,
    education:          form.education,
    previous_companies: form.previousCompanies,
    certifications:     form.certifications,
    projects:           form.projects,
    designation:        form.designation,
    current_ctc:        form.currentCTC,
    expected_ctc:       form.expectedCTC,
    notice_period:      form.noticePeriod,
  };
  if (parseMeta.parseId) payload.parse_id = parseMeta.parseId;
  if (parseMeta.tempId)  payload.temp_id  = parseMeta.tempId;
  return payload;
};

export const SORT_OPTIONS = [
  { id: "HIGHEST_SCORE", label: "Highest ATS Match" },
  { id: "LOWEST_SCORE",  label: "Lowest ATS Match" },
  { id: "EXPERIENCE",    label: "Experience: High to Low" },
];

export const sortApplications = (apps, sortBy) => {
  const list = [...apps];
  const parseExperienceYears = (value) => {
    if (!value) return 0;
    const match = String(value).match(/(\d+(?:\.\d+)?)/);
    return match ? parseFloat(match[1]) : 0;
  };

  switch (sortBy) {
    case "LOWEST_SCORE":
      return list.sort((a, b) => (a.atsScore ?? 0) - (b.atsScore ?? 0));
    case "EXPERIENCE":
      return list.sort((a, b) => parseExperienceYears(b.totalExperience) - parseExperienceYears(a.totalExperience));
    case "HIGHEST_SCORE":
    default:
      return list.sort((a, b) => (b.atsScore ?? 0) - (a.atsScore ?? 0));
  }
};

/**
 * Convert an API application object (snake_case) to the UI's camelCase shape.
 */
export const normalizeApplication = (a) => ({
  id:                a.application_id,
  name:              a.name,
  firstName:         a.first_name,
  lastName:          a.last_name,
  email:             a.email,
  mobile:            a.mobile,
  currentLocation:   a.current_location   || "",
  experienceType:    a.experience_type    || "EXPERIENCED",
  totalExperience:   a.total_experience   || "",
  currentCompany:    toEntryListString(a.current_company)    || "",
  currentCTC:        a.current_ctc        || "",
  expectedCTC:       a.expected_ctc       || "",
  noticePeriod:      a.notice_period      || "",
  applicationSource: a.application_source || "",
  status:            a.status,
  atsScore:          a.ats_score          ?? 0,
  atsMatchLabel:     getAtsMatchLabel(a.ats_score, a.ats_match_label),
  atsBreakdown:      normalizeAtsBreakdown(a.ats_breakdown ?? {}),
  atsScoreReason:    a.ats_score_reason   ?? "",
  aiRecommendation:  a.recommendation     ?? "",
  appliedDate: (() => {
    if (!a.applied_date) return "";
    const p = a.applied_date.split("-");
    // YYYY-MM-DD → DD-MM-YYYY
    return p.length === 3 && p[0].length === 4 ? `${p[2]}-${p[1]}-${p[0]}` : a.applied_date;
  })(),
  resumeUrl:         a.resume_url         || "",
  resumeFileName:    a.resume_file_name   || "",
});

/**
 * Convert an API "my referrals"/"all referrals" list item (snake_case) to the
 * UI's camelCase shape. Reuses the same candidate-field mapping as
 * normalizeApplication, plus the job the referral belongs to (job_id/job_title
 * aren't present on a single-job application response, only on this cross-job
 * listing) and who referred the candidate (only meaningful on the org-wide
 * "All Referrals" list — absent/ignored on the current user's own list).
 */
export const normalizeReferral = (r) => ({
  ...normalizeApplication(r),
  jobId:          r.job_id,
  jobTitle:       r.job_title || "",
  referredByName: r.referred_by_name || "",
});

/** True when a File (or File-like) is a PDF by extension and MIME when present. */
export const isPdfResumeFile = (file) => {
  if (!file?.name) return false;
  const ext = file.name.split(".").pop()?.toLowerCase();
  if (ext !== "pdf") return false;
  const mime = (file.type || "").toLowerCase();
  return !mime || mime === "application/pdf" || mime === "application/octet-stream";
};

/**
 * True when a stored resume can be inlined in an iframe (PDF only).
 * DOC/DOCX URLs force a browser download instead of a preview.
 */
export const isPdfResumeUrl = (url, fileName = "") => {
  const name = (fileName || "").split("?")[0];
  const path = (url || "").split("?")[0];
  const source = name || path;
  if (!source) return false;
  return source.split(".").pop()?.toLowerCase() === "pdf";
};

/**
 * Convert an API job object (snake_case) to the UI's internal camelCase shape.
 * Keeps all child components (JobCard, JobDetailsPage, CreateJobModal) unchanged.
 */
export const normalizeJob = (apiJob) => {
  let publishedDate = "";
  if (apiJob.published_date) {
    const [y, m, d] = apiJob.published_date.split("-");
    publishedDate = `${d}-${m}-${y}`;
  }
  return {
    id:                apiJob.job_id,
    title:             apiJob.job_title,
    department:        apiJob.department,
    designation:       apiJob.designation,
    employmentType:    apiJob.employment_type,
    location:          apiJob.location,
    experience:        apiJob.experience_required,
    vacancies:         apiJob.vacancies,
    skills: apiJob.required_skills
      ? String(apiJob.required_skills).split(",").map((s) => s.trim()).filter(Boolean)
      : [],
    description:       apiJob.job_description,
    publishedDate,
    closingDate:       apiJob.closing_date || "",
    status:            apiJob.status,
    totalApplications: apiJob.total_applications || 0,
    shortlisted:       apiJob.shortlisted_count || 0,
    hired:             apiJob.hired_count ?? apiJob.hired ?? 0,
    rejected:          apiJob.rejected_count ?? apiJob.rejected ?? 0,
  };
};

/* ── Resume import status helpers ─────────────────────────── */

export const IMPORT_STATUS = {
  READY:        "READY",
  NEEDS_REVIEW: "NEEDS_REVIEW",
  FAILED:       "FAILED",
  DUPLICATE:    "DUPLICATE",
};

const IMPORT_MANDATORY = ["name", "email", "phone"];

export const determineImportStatus = (parsed, raw = {}) => {
  const explicit = raw.candidate_status || raw.import_status;
  if (explicit) {
    const s = String(explicit).toUpperCase().replace(/\s+/g, "_");
    if (Object.values(IMPORT_STATUS).includes(s)) return s;
  }
  if (raw.is_duplicate || raw.isDuplicate) return IMPORT_STATUS.DUPLICATE;
  if (!parsed.name?.trim() && !parsed.email?.trim()) return IMPORT_STATUS.FAILED;
  const hasMissing = IMPORT_MANDATORY.some((f) => !parsed[f]?.trim());
  // A badly-parsed resume (numeric-only name, malformed email/phone, garbage
  // CTC/notice-period) shouldn't be auto-saved as READY — route it to Needs
  // Review so a human fixes it through the validated review form instead.
  const hasInvalid = Object.keys(getReviewFormErrors(parsed)).length > 0;
  return (hasMissing || hasInvalid) ? IMPORT_STATUS.NEEDS_REVIEW : IMPORT_STATUS.READY;
};

export const getImportMissingFields = (form) =>
  IMPORT_MANDATORY.filter((f) => !form[f]?.trim());

const REVIEW_NUMERIC_FIELDS = [
  ["currentCTC",   "Current CTC"],
  ["expectedCTC",  "Expected CTC"],
  ["noticePeriod", "Notice period"],
];

/**
 * Format/character validation for the resume-import review form (Needs
 * Review / Duplicate tabs). Presence of name/email/phone is already handled
 * separately by getImportMissingFields — this only flags values that ARE
 * present but wrong-shaped: a name that's just digits, a malformed email,
 * or a CTC/notice-period value that contains letters or a negative number.
 */
export const getReviewFormErrors = (form = {}) => {
  const errs = {};

  const name = form.name?.trim();
  if (name && isPurelyNumeric(name)) errs.name = "Name cannot contain only numbers";

  const email = form.email?.trim();
  if (email && !EMAIL_PATTERN.test(email)) errs.email = "Enter a valid email address";

  const phone = form.phone?.trim();
  if (phone && !MOBILE_PATTERN.test(phone)) errs.phone = "Enter a valid phone number";

  const designation = form.designation?.trim();
  if (designation && isPurelyNumeric(designation)) errs.designation = "Designation cannot contain only numbers";

  REVIEW_NUMERIC_FIELDS.forEach(([key, label]) => {
    const err = validateNumericField(form[key], label, { required: false });
    if (err) errs[key] = err;
  });

  return errs;
};

/**
 * Broader, display-only view of "what's blank" for the Needs Review grid —
 * lets HR see at a glance what to fill without opening every candidate.
 * Purely cosmetic: does NOT affect determineImportStatus / mandatory-field
 * validation, which remains name/email/phone only (IMPORT_MANDATORY).
 */
export const DISPLAY_MISSING_FIELD_LABELS = {
  name:        "Name",
  email:       "Email",
  phone:       "Phone Number",
  experience:  "Experience",
  designation: "Designation",
};

export const getDisplayMissingFields = (candidate = {}) =>
  Object.keys(DISPLAY_MISSING_FIELD_LABELS).filter((k) => !String(candidate[k] || "").trim());

/**
 * Turns a backend-provided duplicate match reason into an HR-readable label.
 * Falls back to null when the backend doesn't send one, so callers can
 * decide whether to show a generic "Duplicate" indicator instead.
 */
export const formatDuplicateReason = (reason) => {
  if (!reason) return null;
  const r = String(reason).toUpperCase();
  if (r.includes("EMAIL")) return "Duplicate Email";
  if (r.includes("PHONE") || r.includes("MOBILE")) return "Duplicate Phone Number";
  if (r.includes("NAME")) return "Duplicate Name";
  return `Duplicate (${reason})`;
};

export const isImportCandidateSaved = (candidate) =>
  Boolean(
    candidate?.saved
    || String(candidate?.importStatus || candidate?.candidate_status || "").toUpperCase() === "SAVED"
  );

export const computeImportCounts = (pendingCandidates = [], failedFiles = []) => ({
  NEEDS_REVIEW: pendingCandidates.filter((c) => c.importStatus === IMPORT_STATUS.NEEDS_REVIEW).length,
  DUPLICATE:    pendingCandidates.filter((c) => c.importStatus === IMPORT_STATUS.DUPLICATE).length,
  FAILED:       failedFiles.length,
});

export const normalizeImportCandidateRow = (item, idx = 0) => {
  const pd = item.parsed_data || {};
  const rawFlat = {
    ...pd,
    previous_companies: pd.previous_companies ?? pd.previousCompanies ?? pd.companies,
    ats_score:          item.ats_score          ?? pd.ats_score,
    ats_breakdown:      item.ats_breakdown       ?? pd.ats_breakdown,
    ats_score_reason:   item.ats_score_reason    ?? pd.ats_score_reason,
    recommendation:     item.recommendation      ?? pd.recommendation,
    resume_preview_url: item.resume_preview_url  ?? item.resume_url,
    resume_file_name:   item.resume_file_name    ?? pd.resume_file_name,
    parse_id:           item.parse_id            ?? item.temp_id,
    current_ctc:        item.current_ctc         ?? pd.current_ctc,
    expected_ctc:       item.expected_ctc        ?? pd.expected_ctc,
    notice_period:      item.notice_period       ?? pd.notice_period,
  };
  const parsed = normalizeParsedCandidate(rawFlat);
  const status = determineImportStatus(parsed, item);
  const saved  = Boolean(
    item.is_saved || item.saved
    || String(item.candidate_status || item.import_status || "").toUpperCase() === "SAVED"
  );
  return {
    id:                item.import_candidate_id ?? item.importCandidateId ?? `cand-${item.temp_id ?? item.tempId ?? idx}`,
    importStatus:      status,
    saved,
    resumeName:        parsed.resumeFileName || item.file_name || item.fileName || `Resume ${idx + 1}`,
    name:              parsed.name,
    email:             parsed.email,
    phone:             parsed.phone,
    skills:            parsed.skills,
    experience:        parsed.experience,
    education:         parsed.education,
    previousCompanies: parsed.previousCompanies,
    certifications:    parsed.certifications,
    projects:          parsed.projects,
    designation:       parsed.designation,
    currentCTC:        parsed.currentCTC,
    expectedCTC:       parsed.expectedCTC,
    noticePeriod:      parsed.noticePeriod,
    atsScore:          parsed.atsScore,
    atsMatchLabel:     parsed.atsMatchLabel,
    atsBreakdown:      parsed.atsBreakdown,
    atsScoreReason:    parsed.atsScoreReason,
    aiRecommendation:  parsed.aiRecommendation,
    parseId:           parsed.parseId,
    resumeUrl:         parsed.resumeUrl,
    tempId:            item.temp_id ?? item.tempId ?? null,
    sessionId:         item.session_id ?? item.sessionId ?? null,
    existingCandidate: item.existing_candidate || item.existingCandidate || null,
    duplicateReason:   item.duplicate_reason ?? item.duplicateReason ?? item.match_type ?? item.matchType ?? null,
    failureReason:     item.failure_reason ?? item.failureReason ?? null,
  };
};

export const normalizeImportResults = (data) => {
  const rawCandidates = Array.isArray(data?.parsed_candidates)
    ? data.parsed_candidates
    : Array.isArray(data?.candidates)
      ? data.candidates
      : Array.isArray(data) ? data : [];

  const rawFailed = data?.failed_files || [];

  const normalized = rawCandidates.map((item, idx) => normalizeImportCandidateRow(item, idx));

  const fromCandsFailed = normalized
    .filter((c) => c.importStatus === IMPORT_STATUS.FAILED)
    .map((c) => ({
      id:       c.id,
      fileName: c.resumeName,
      reason:   c.failureReason || "No readable content detected",
      importStatus: IMPORT_STATUS.FAILED,
    }));

  const fromApiFailed = rawFailed.map((f, i) => ({
    id:           f.file_id || f.import_candidate_id || `apifail-${i}`,
    fileName:     f.file_name || f.fileName || `File ${i + 1}`,
    reason:       f.reason || f.error || "Could not be parsed",
    importStatus: IMPORT_STATUS.FAILED,
  }));

  const validCandidates = normalized.filter((c) => c.importStatus !== IMPORT_STATUS.FAILED);
  const failedFiles = [...fromApiFailed, ...fromCandsFailed];

  return { validCandidates, failedFiles, sessionId: data?.session_id || null };
};
